import axios from 'axios';
import { isEmpty } from 'lodash';
import { LocationSearch } from '../containers/SportsBookContainer';
import { AffiliateData } from '../data-types/affiliates/affiliatesInterfaces';
import { preferredLanguage } from '../data-types/cookies/cookiesConstants';
import { ERROR_TYPE } from '../data-types/error/errorEnums';
import { GetScoreResponse } from '../data-types/feature-flags/featureFlagsInterfaces';
import { auth, DEFAULT_ERROR_MESSAGE } from '../data-types/initialisation/initialisationConstants';
import { CommandResponseError } from '../data-types/initialisation/initialisationInterfaces';
import { AcceptedCountries } from '../data-types/international/internationalEnums';
import { POST_HOG_EVENT } from '../data-types/posthog/posthogEnums';
import {
  MarketingPreferencesResponse,
  SetUserMarketingPreferencesResponse,
} from '../data-types/profile/profile-sections/marketing-preferences/marketingPreferencesInterfaces';
import { LimitType, PRODUCT_BLOCK_ID } from '../data-types/profile/profile-sections/staying-in-control/stayingInControlEnums';
import { ReferralContext } from '../data-types/referral/referralInterfaces';
import { featuredCategories } from '../data-types/sports/sportsConstants';
import { TOKEN_RENEW_AFTER_TIME } from '../data-types/timing/timingConstants';
import { UserQuestionsTypes, UserStatus } from '../data-types/user/userEnums';
import {
  AccessBlockListResponse,
  AccessBlockResponse,
  AccessUnBlockResponse,
  CloseAccountResponse,
  DetailsWebResponse,
  ForgottenPasswordResponse,
  GetUserEmploymentPendingQuestionsResponse,
  GetUserStatsResponse,
  LimitResponse,
  LoginPayload,
  LoginResponse,
  LogReferralResponse,
  MarketingPreferencesData,
  ProofRequest,
  RealityCheckResponse,
  ReferralUrlResponse,
  RegisterDetailsResponse,
  RequestCodePayload,
  SessionLogoutResponse,
  SessionRenewResponse,
  SetPhoneUUIDResponse,
  SetRealityCheckResponse,
  SetUserEmploymentQuestionsAnswersPayload,
  SetUserEmploymentQuestionsAnswersResponse,
  SetUserLanguageResponse,
  SetUserZendeskTokenResponse,
  TermsAcceptResponse,
  TermsGetNewResponse,
  TermsGetResponse,
  UpdateUserArgs,
  UserActivateResponse,
  UserProofRequestsOverviewResponse,
  VerifyPhoneUUIDResponse,
} from '../data-types/user/userInterfaces';
import { SIGNUP_OPTION } from '../data-types/wallet/walletEnums';
import { RegistrationUserPathResponse, UserDuplicatedPhoneNumberResponse } from '../data-types/wallet/walletInterfaces';
import { fingerprint, SOCKET_ADDRESS } from '../initialisation/initialisation';
import {
  getLanguageByCountry,
  handleSettingsFetch,
  isFirstDepositMandatoryForUserCountry,
  isGBUser,
  setBetkwiffFlag,
  setGeoLocationInfo,
} from '../international/internationalUtils';
import { PATHS } from '../routing-system/appPaths';
import browserHistory from '../routing-system/browserHistory';
import { trackLoginWithCredentials, trackSessionLogin } from '../services/trackers';
import { getTransunionBlackBox } from '../services/transunion';
import { logoutZendeskUser } from '../services/zendesk';
import {
  accessBlock,
  accessBlocksList,
  accessConfirmUnblock,
  accessUnblock,
  documentsGetDocsOverview,
  limitGet,
  limitSet,
  obsGetMoodbarScore,
  personalStatsGet,
  sessionLogout,
  sessionRenew,
  sessionUnlock,
  useForgotPassword,
  userActivate,
  userConfirmAccountClosure,
  userDefaultStake,
  userDetailsWebCommand,
  userDisableDeposits,
  userEnableDeposits,
  userGetPendingQuestions,
  userGetReferralUrl,
  userLogReferral,
  userOddsType,
  userRealityResponse,
  userRegisterDetails,
  userRegistrationUserPath,
  userResetPassword,
  userSetLanguage,
  userSetMarketingPreferences,
  userSetPhoneUuid,
  userSetRealityCheck,
  userSubmitQuestionsAnswers,
  userTermsAccept,
  userTermsGetNew,
  userUpdatePassword,
  userVerifyPhoneUuid,
  userZendeskAuthenticate,
} from '../socket-types/userScoketTypes';
import { updatePendingBets } from '../state/actions/betActions';
import { setMoodBarScore } from '../state/actions/customizationActions';
import { setErrorDialog, setIsInboxOpen, setUserConfirmProductUnblockDialog } from '../state/actions/dialogActions';
import { setCurrentSignUpPath, setSignupCheckpoint, setSignUpDialogOpen } from '../state/actions/signupActions';
import { setTriggerSportsbookResubscriptions } from '../state/actions/sportsbookActions';
import { resetState } from '../state/actions/stateActions';
import {
  clearUserDetails,
  setAuthentication,
  setInboxBadge,
  setLocale,
  setTermsInfo,
  setUserDetails,
  setUserLocation,
} from '../state/actions/userDataActions';
import { store } from '../state/store';
import { getCookie } from '../utils/incomeAccess';
import { performPostLogoutTasks } from '../utils/logout';
import { checkPostHogFeaturedFlags, postHogCapture, postHogIdentifyUser, postHogStartSessionRecording } from '../utils/posthog';
import { enqueueSnackbarPrompt } from '../utils/prompts';
import { getLocationSearchData, getSportIdAndSectionName } from '../utils/queryString';
import { getSportIdBySportSEOUrl } from '../utils/seo';
import { hasLoggedInAfterSuccessSignup, removeLoggedInAfterSuccessSignup } from '../utils/signup';
import { adjustAcknowledgeDepositFlagIfNeeded, setBasicUserInformation, setCountrySettingsByUserLocation } from '../utils/user';
import { tryToGetWalletSettings } from '../utils/wallet';
import { attemptToLoadInboxBadge, checkShowPushPermissionDialog, setXtremepushUserId } from '../utils/xtremepush';
import { sendCommand } from './commandsHandler';
import { checkFeatureFlags } from './featureFlagsCommands';
import { menuMain } from './menuCommands';
import { fetchTrendingBets, getEvent, resubscribeToDetailedEvent, updateEvents, updateEventsForMultiSportsLists } from './sportsCommands';
import { acknowledgeDepositInformation, getBalance } from './walletCommands';

export const login = async (loginPayload: LoginPayload): Promise<boolean> => {
  const { signupV3 } = store.getState().signup;

  if (signupV3.useSignupV3) {
    return await newLogin(loginPayload);
  }

  return await legacyLogin(loginPayload);
};

export const legacyLogin = async (loginPayload: LoginPayload): Promise<boolean> => {
  const res = await sendCommand<LoginResponse>('session:login', loginPayload);
  const success = await setUpUser(res, true);

  if (success) {
    handlePostAuthTasks();
    checkShowPushPermissionDialog();
  }
  return success;
};

export const newLogin = async (loginPayload: LoginPayload): Promise<boolean> => {
  const res = await sendCommand<LoginResponse>('signupv3:login', loginPayload);

  if (res && res.data && res.data.nextCheckpoint) {
    setSignupCheckpoint(
      adjustAcknowledgeDepositFlagIfNeeded(res.data.nextCheckpoint, res.data.user.acknowledgedDepositInformationAt || undefined),
    );
    setBasicUserInformation(res.data.user);
    setSignUpDialogOpen(true);
    return false;
  }

  const success = await setUpUser(res, true);

  if (success) {
    handlePostAuthTasks();
    checkShowPushPermissionDialog();
  }
  return success;
};

const canMakeRequestsDependingOnCountryAndStatus = (country: string | undefined, status: number | undefined) => {
  const acceptedStatusesForNonGBUsers = [UserStatus.ACTIVE, UserStatus.UNVERIFIED, UserStatus.VERIFIED];

  if (country && status) {
    if (country === AcceptedCountries.GREAT_BRITAIN && status === UserStatus.ACTIVE) {
      return true;
    }
    if (country !== AcceptedCountries.GREAT_BRITAIN && acceptedStatusesForNonGBUsers.includes(status)) {
      return true;
    }
  }
  return false;
};

export const isUserEligibleForAuthenticatedRequests = () => {
  const {
    userData: { user },
  } = store.getState();

  if (user && user.address) {
    return canMakeRequestsDependingOnCountryAndStatus(user.address.country, user.status);
  }

  return false;
};

export const handlePostAuthTasks = async () => {
  const {
    userData: { user },
    customization: { featureFlags },
  } = store.getState();

  checkPostHogFeaturedFlags();

  let menuMainCallback = menuMain;

  if (user && !isNaN(Number(user.id))) {
    trackSessionLogin(Number(user.id), user.email || '');

    menuMainCallback = () => menuMain(user.id);
  }

  const postAuthFunctions = [menuMainCallback];

  if (featureFlags.moodBar && isUserEligibleForAuthenticatedRequests()) {
    postAuthFunctions.push(fetchMoodBarScore);
  }
  if (featureFlags.trendingBets) {
    postAuthFunctions.push(fetchTrendingBets);
  }

  await Promise.all(postAuthFunctions.map(postAuthFunction => postAuthFunction())).catch(() => {
    // do nothing
  });
};

export const handlePostLogoutTasks = async () => {
  const postAuthFunctions = [tryToGetWalletSettings, menuMain];

  await Promise.all(postAuthFunctions.map(postAuthFunction => postAuthFunction())).catch(() => {
    // do nothing
  });
};

export const unlockPendingBets = () => {
  const {
    bets: { pendingBets },
  } = store.getState();

  if (pendingBets.length) {
    pendingBets.forEach(async bet => {
      const event = await getEvent(bet.event.sportId, bet.event.id);
      updatePendingBets({ ...bet.event, ...event });
    });
  }
};

const handleDesktopSplitUiGetter = (search: LocationSearch) => {
  const { sport, section, subSection } = search;
  const sportIdString = String(getSportIdBySportSEOUrl(sport));
  if ([featuredCategories.IN_PLAY, featuredCategories.FEATURED].includes(sportIdString)) {
    updateEventsForMultiSportsLists(sportIdString);
    resubscribeToDetailedEvent();
  } else if (section) {
    const { sportId, sectionName } = getSportIdAndSectionName(sportIdString, section);
    updateEvents(sportId, sectionName, subSection);
    resubscribeToDetailedEvent();
  }
};

const handleSimpleEventListGetter = () => {
  const pathname = window.location.pathname;
  if (pathname.includes('sports')) {
    const indexOfSecondSlash = pathname.split('/', 2).join('/').length;
    const pathParams = pathname.substring(indexOfSecondSlash + 1).split('/');
    if (pathParams.length === 1) {
      // default/in-play
      const sportIdString = String(getSportIdBySportSEOUrl(pathParams[0]));
      updateEventsForMultiSportsLists(sportIdString);
    } else if (pathParams.length === 2) {
      // a sport and in-play/24hrs
      const sportIdString = String(getSportIdBySportSEOUrl(pathParams[0]));
      const section = pathParams[1];
      const { sportId, sectionName } = getSportIdAndSectionName(sportIdString, section);
      updateEvents(sportId, sectionName);
    } else if (pathParams.length === 3) {
      // a sport id and calendar/competition id
      const sportIdString = String(getSportIdBySportSEOUrl(pathParams[0]));
      const section = pathParams[2];
      const { sportId, sectionName } = getSportIdAndSectionName(sportIdString, section);
      updateEvents(sportId, sectionName);
    } else {
      updateEvents(0, featuredCategories.FEATURED);
    }
  }
};

export const unlockGetEvents = () => {
  if (isEmpty(store.getState().sportsbookEvents)) {
    const search = getLocationSearchData(window.location.search);
    if (search) {
      handleDesktopSplitUiGetter(search);
    } else {
      handleSimpleEventListGetter();
    }
  }
};

export const unlockSession = async (token?: string): Promise<boolean> => {
  const justLoggedInAfterSignup = hasLoggedInAfterSuccessSignup();

  if (justLoggedInAfterSignup) {
    removeLoggedInAfterSuccessSignup();
  }

  setTriggerSportsbookResubscriptions(true);
  const res = await sendCommand<LoginResponse>(sessionUnlock, { token: token || localStorage.sessionToken });

  if (res.data && res.data.user && !res.data.user.status) {
    // NOTE: the user was somewhere in the middle of signup and should log in again for safety reasons
    resetState();
    setBetkwiffFlag();
    await setGeoLocationInfo();
    handleSettingsFetch();
    unlockGetEvents();
    return true;
  }

  try {
    await setGeoLocationInfo();
    await handleSettingsFetch();
    await setUpUser(res);
    unlockPendingBets();
  } catch (error) {
    // NOTE: Something went wrong, so reset the state
    resetState();
    setBetkwiffFlag();
    await setGeoLocationInfo();
    handleSettingsFetch();
    unlockGetEvents();
    setInboxBadge(0);
    setIsInboxOpen(false);
    setXtremepushUserId(undefined);
    logoutZendeskUser();
    return false;
  }

  if (token || localStorage.sessionToken) {
    handlePostAuthTasks();
  }

  if (localStorage.sessionToken && justLoggedInAfterSignup) {
    trackLoginWithCredentials();
  }

  return true;
};

export const setUpUserLocationAndLanguage = () => {
  const user = store.getState().userData.user;

  if (user && user.address) {
    const country = user.address.country as AcceptedCountries;
    const locale = getCookie(preferredLanguage) || getLanguageByCountry(country);
    setLocale(locale);
    setUserLocation(country);
  }
};

export const setUpUser = async (res: LoginResponse, posthogIdentify = false): Promise<boolean> => {
  if (res.data && res.data.user) {
    const state = store.getState();
    const { userLocation } = state.userData;
    const { signupV3 } = state.signup;
    const { useSignupV3 } = signupV3;
    const userId = res.data.user.id || res.data.user.userId;
    setTokenDetails(res.data.token || localStorage.sessionToken);
    setBasicUserInformation(res.data.user);

    if (
      res.data.user.status === UserStatus.CREATED &&
      userLocation === AcceptedCountries.FINLAND &&
      res.data.user.acceptedTermsId &&
      res.data.user.email !== null &&
      res.data.user.firstDepositAt === null
    ) {
      browserHistory.replace(PATHS.SIGNUP.ZIMPLER_DEPOSIT_CHOICE);
      return false;
    }
    if (res.data.user.status === UserStatus.CREATED && !useSignupV3) {
      setErrorDialog('You have not completed the sign up process. Please complete this before logging in.');
      setAuthentication(auth.SIGNUP);

      setCurrentSignUpPath(PATHS.SIGNUP.STEPS.EMAIL);
      browserHistory.replace(PATHS.SIGNUP.STEPS.EMAIL);
      return false;
    }

    if (res.data.user.status === UserStatus.CREATED && useSignupV3) {
      return false;
    }

    await getUser();
    setUpUserLocationAndLanguage();
    setCountrySettingsByUserLocation();
    setXtremepushUserId(userId);
    await tryToGetWalletSettings();

    const unverifiedGBUser = res.data.user.status === UserStatus.UNVERIFIED && isGBUser();
    if (unverifiedGBUser) {
      if (!useSignupV3) {
        setAuthentication(auth.SIGNUP);
        setCurrentSignUpPath(PATHS.SIGNUP.STEPS.UNVERIFIED);
        browserHistory.replace(PATHS.SIGNUP.STEPS.UNVERIFIED);
      }
      return false;
    }

    const mandatoryFirstDepositMissing = !res.data.user.firstDepositAt && isFirstDepositMandatoryForUserCountry();
    if (mandatoryFirstDepositMissing) {
      if (!useSignupV3) {
        setAuthentication(auth.SIGNUP);
        setCurrentSignUpPath(PATHS.SIGNUP.STEPS.DEPOSIT_LIMIT);
        browserHistory.replace(PATHS.SIGNUP.STEPS.DEPOSIT_LIMIT);
      }
      return false;
    }

    await getBalance();
    await termsAccepted();
    await getBlocks();

    setAuthentication(auth.AUTHENTICATED);
    await checkFeatureFlags();
    attemptToLoadInboxBadge();

    const {
      userData: { user },
    } = store.getState();
    if (user && !user.acknowledgedDepositInformationAt) {
      await acknowledgeDepositInformation();
      setUserDetails({ acknowledgedDepositInformationAt: new Date().toString() });
    }

    if (user && posthogIdentify) {
      postHogIdentifyUser(userId, {
        userId,
        email: user.email || user.phoneNumber || '',
      });
    }

    postHogStartSessionRecording();

    return true;
  }

  // handle expected errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }
  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const getUser = async (): Promise<void> => {
  const res = await sendCommand<DetailsWebResponse>(userDetailsWebCommand, {});

  // response when call is successful
  if (res.data && (res.data.email || res.data.phoneNumber)) {
    return setUserDetails({
      acceptedTermsId: res.data.acceptedTermsId,
      email: res.data.email,
      firstDepositAt: res.data.firstDepositAt,
      fractionalOdds: res.data.fractionalOdds,
      status: res.data.status,
      name:
        {
          title: (res.data.name && res.data.name.title) || undefined,
          firstName: (res.data.name && res.data.name.firstName) || undefined,
          lastName: (res.data.name && res.data.name.surname) || undefined,
        } || undefined,
      defaultStake: res.data.defaultStake,
      dateOfBirth: res.data.dateOfBirth,
      address: res.data.address,
      realityCheckInterval: res.data.realityCheckInterval,
      hasBlockedCasino: res.data.accessBlocks && res.data.accessBlocks.some(e => e.productId === PRODUCT_BLOCK_ID.CASINO),
      hasBlockedSportsBook: res.data.accessBlocks && res.data.accessBlocks.some(e => e.productId === PRODUCT_BLOCK_ID.SPORTSBOOK),
      marketing: {
        phone: res.data.allowsPhone,
        email: res.data.allowsEmail,
        push: res.data.allowsPushNotification,
        sms: res.data.allowsSMS,
      },
      depositsDisabled: !res.data.depositsEnabled,
      depositsEnabledAt: res.data.depositsEnabledAt,
      referralEnabled: res.data.referralEnabled,
      currency: {
        symbol: res.data.currency.symbol,
        isoAlphabeticCode: res.data.currency.isoAlphabeticCode,
      },
      phoneNumber: res.data.phoneNumber,
      acknowledgedDepositInformationAt: res.data.acknowledgedDepositInformationAt,
      language: res.data.language || undefined,
    });
  }

  // handle errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  throw new Error('Something went wrong whilst getting the user');
};

export const getBlocks = async (): Promise<void> => {
  const res = await sendCommand<AccessBlockListResponse>(accessBlocksList, {});
  // handle errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  if (res.data) {
    res.data.blocks.forEach(block => {
      if (
        (block.productId === PRODUCT_BLOCK_ID.SPORTSBOOK || block.productId === PRODUCT_BLOCK_ID.CASINO) &&
        new Date(block.expiresAt) < new Date() &&
        !block.userConfirmed
      ) {
        // do something with the product id
        setUserConfirmProductUnblockDialog(true);
      }
    });
  }
};

export const updateUser = async (data: UpdateUserArgs, postHogUpdateIdentifiers = false): Promise<void> => {
  const res = await sendCommand<RegisterDetailsResponse>(userRegisterDetails, { uuid: fingerprint, updates: data });
  // response when call is successful
  if (res.data && (res.data.email || res.data.phoneNumber)) {
    if (postHogUpdateIdentifiers) {
      postHogIdentifyUser(res.data.id, { userId: res.data.id, email: res.data.email || res.data.phoneNumber || '' });
    }
    return setUserDetails({
      id: res.data.id,
      address: {
        addressLine1: res.data.address.addressLine1 || undefined,
        addressLine2: res.data.address.addressLine2 || undefined,
        buildingNumber: res.data.address.buildingNumber || undefined,
        county: res.data.address.county || undefined,
        postCode: res.data.address.postCode || undefined,
        town: res.data.address.town || undefined,
        country: res.data.address.country || undefined,
      },
      acceptedTermsId: res.data.acceptedTermsId || undefined,
      email: res.data.email,
      firstDepositAt: res.data.firstDepositAt || undefined,
      fractionalOdds: res.data.fractionalOdds,
      status: res.data.status,
      defaultStake: res.data.defaultStake,
      name:
        {
          title: (res.data.name && res.data.name.title) || undefined,
          firstName: (res.data.name && res.data.name.firstName) || undefined,
          lastName: (res.data.name && res.data.name.surname) || undefined,
        } || undefined,
      currency: { ...res.data.currency },
    });
  }

  const hasDuplicateAccountError = res.error && res.error.data && res.error.data.code && res.error.data.code === ERROR_TYPE.DUPLICATE_USER;

  if (hasDuplicateAccountError) {
    postHogCapture(POST_HOG_EVENT.DUPLICATE_ACCOUNT, { email: data.email || '' });
  }

  // handle errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const setUserMarketingPreferences = async (data: MarketingPreferencesData): Promise<void> => {
  const res = await sendCommand<SetUserMarketingPreferencesResponse>('user:setmarketingpreferences', {
    ...data,
    uuid: fingerprint,
  });
  // handle errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  // response when call is successful
  if (res.data) {
    return;
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const acceptLatestTermsSignUp = async (countryCode: AcceptedCountries): Promise<void> => {
  const res = await sendCommand<TermsGetNewResponse>(userTermsGetNew, { countryCode, uuid: fingerprint, termsType: 1 });

  // handle errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  // response when call is successful
  if (res.data && res.data.newTerms && res.data.newTerms.id) {
    const termsAcceptResponse = await sendCommand<TermsAcceptResponse>(userTermsAccept, {
      uuid: fingerprint,
      termsId: res.data.newTerms.id,
    });

    // handle errors from the BE
    if (termsAcceptResponse.error && termsAcceptResponse.error.notification && termsAcceptResponse.error.notification.userFriendlyMessage) {
      throw new Error(termsAcceptResponse.error.notification.userFriendlyMessage);
    }

    if (termsAcceptResponse.data && termsAcceptResponse.data.success) {
      return;
    }
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const startPhoneVerification = async (data: { phoneNumber: string; affiliate?: AffiliateData }): Promise<void> => {
  const res = await sendCommand<SetPhoneUUIDResponse>(userSetPhoneUuid, { ...data, uuid: fingerprint });

  const hasDuplicateAccountError = res.error && res.error.data && res.error.data.code && res.error.data.code === ERROR_TYPE.DUPLICATE_USER;

  if (hasDuplicateAccountError) {
    postHogCapture(POST_HOG_EVENT.DUPLICATE_ACCOUNT, { email: data.phoneNumber });
  }

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  if (res.data) {
    return;
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const verifyPhoneNumberWithCode = async (data: { verificationCode: string; phoneNumber?: string }): Promise<void> => {
  const res = await sendCommand<VerifyPhoneUUIDResponse>(userVerifyPhoneUuid, { ...data, uuid: fingerprint });

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  if (res.data) {
    return;
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const activateUser = async (): Promise<CommandResponseError | void> => {
  const res = await sendCommand<UserActivateResponse>(userActivate, {
    uuid: fingerprint,
    device: 'iPhone', // TODO: Change this to web when the BE can handle it
    deviceRecognitionString: getTransunionBlackBox(),
  });
  // handle errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  // response when call is successful
  if (res.data && res.data.user) {
    return setUserDetails({
      status: res.data.user.status,
    });
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const disableUserDeposits = async () => {
  const res = await sendCommand<LimitResponse>(userDisableDeposits, {});
  // handle errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  // response when call is successful
  if (res.data) {
    // update users details on gloabl user object
    await setUserDetails({
      depositsDisabled: true,
    });
    return;
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const termsAccepted = async (): Promise<void> => {
  const res = await sendCommand<TermsGetResponse>(userTermsGetNew, {});
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    enqueueSnackbarPrompt(res.error.notification.userFriendlyMessage, { closeOnClick: true });
    return;
  }
  const user = store.getState().userData.user;
  if (user && user.acceptedTermsId && user.acceptedTermsId < res.data.newTerms.id) {
    setTermsInfo({ accepted: false, newTerms: res.data.newTerms });
  } else {
    setTermsInfo({ accepted: true, newTerms: undefined });
  }
};

export const acceptTerms = async (): Promise<void> => {
  const {
    userData: {
      terms: { newTerms },
    },
  } = store.getState();

  const res = await sendCommand<TermsAcceptResponse>(userTermsAccept, {
    termsId: newTerms ? newTerms.id : undefined,
  });

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  // response when call is successful
  if (res.data) {
    return;
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const setUserRealityCheck = async (data: { realityCheckInterval: number }): Promise<void> => {
  const res = await sendCommand<SetRealityCheckResponse>(userSetRealityCheck, data);

  // handle errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  // response when call is successful
  if (res.data) {
    // update users details on gloabl user object
    return setUserDetails({
      realityCheckInterval: data.realityCheckInterval, // as we don't get a response back from the BE - set the global state as the passed value
    });
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const blockUser = async (data: { password: string; reasonId: number; productId: number; duration?: string }): Promise<void> => {
  const res = await sendCommand<AccessBlockResponse>(accessBlock, data);

  // handle errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  // response when call is successful
  if (res.data) {
    await getUser();
    return;
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const unBlockUser = async (data: { password: string; reasonId: number; productId: number }): Promise<void> => {
  const res = await sendCommand<AccessUnBlockResponse>(accessUnblock, data);

  // handle errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  // response when call is successful
  if (res.data && res.data.success) {
    return;
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const confirmUnblockUser = async (data: { productId: number; reasonId: number; confirmed: boolean }) => {
  const res = await sendCommand<AccessUnBlockResponse>(accessConfirmUnblock, data);

  // handle errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  // response when call is successful
  if (res.data && res.data.success) {
    return;
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const requestCode = async (user: RequestCodePayload): Promise<void> => {
  const res = await sendCommand<ForgottenPasswordResponse>(useForgotPassword, user);
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }
  if (res.data && res.data.user) {
    return;
  }
  return;
};

export const resetPassword = async (user: { code: string; password: string }): Promise<void> => {
  const res = await sendCommand<ForgottenPasswordResponse>(userResetPassword, user);
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }
  if (res.data && res.data.user) {
    return;
  }
  return;
};

export const updateDefaultStake = async (defaultStake: number): Promise<void> => {
  const res = await sendCommand<{ error?: CommandResponseError; data: {} }>(userDefaultStake, { defaultStake });
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }
  if (res.data) {
    await setUserDetails({
      defaultStake,
    });
    return;
  }
  return;
};

export const updateFractionalOdds = async (oddsId: number): Promise<void> => {
  const res = await sendCommand<{ error?: CommandResponseError; data: {} }>(userOddsType, { id: oddsId });
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }
  if (res.data) {
    await setUserDetails({
      oddsTypeId: oddsId,
    });
    return;
  }
  return;
};

export const setMarketingPreferences = async (preferences: MarketingPreferencesData): Promise<void> => {
  const res = await sendCommand<MarketingPreferencesResponse>(userSetMarketingPreferences, preferences);

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  if (res.data) {
    setUserDetails({
      marketing: { ...preferences },
    });
  }
};

export const realityCheckRespond = async (confirm: boolean): Promise<void> => {
  const res = await sendCommand<RealityCheckResponse>(userRealityResponse, { confirm });
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }
  return;
};

export const logoutUser = async (): Promise<void> => {
  const res = await sendCommand<SessionLogoutResponse>(sessionLogout, {});

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  if (res && res.data) {
    performPostLogoutTasks(false);
    return;
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const logReferral = async (referralContext: ReferralContext): Promise<void> => {
  const REFERRAL_LOG_ERROR_MESSAGE = 'Oops, something went wrong. Please retry your invite link and if the problem persists, contact us.';

  try {
    const res = await sendCommand<LogReferralResponse>(userLogReferral, { ...referralContext, uuid: fingerprint });

    if (!res || !res.data || !res.data.success) {
      throw new Error(REFERRAL_LOG_ERROR_MESSAGE);
    }
  } catch (error) {
    throw new Error(REFERRAL_LOG_ERROR_MESSAGE);
  }
};

export const closeAccount = async (data: { reason: number; password: string }): Promise<void> => {
  const res = await sendCommand<CloseAccountResponse>(userConfirmAccountClosure, data);

  if (res.error) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  setAuthentication(auth.UNAUTHENTICATED);
  clearUserDetails();
  return browserHistory.push(PATHS.APP.ROOT);
};

export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  const res = await sendCommand<{ error?: CommandResponseError }>(userUpdatePassword, { currentPassword, newPassword });

  // handle errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  return;
};

export const getReferralUrl = async (): Promise<string> => {
  const res = await sendCommand<ReferralUrlResponse>(userGetReferralUrl, {});

  if (!res.error && res.data && res.data.success && res.data.url) {
    return res.data.url;
  }

  // handle errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

const fetchMoodBarScore = async () => {
  try {
    const response = await sendCommand<GetScoreResponse>(obsGetMoodbarScore, {});
    if (response && response.data && !isNaN(response.data.score)) {
      setMoodBarScore(response.data.score);
    }
  } catch (e) {
    // do nothing
  }
};

export const setLimit = async (amount: number | null, type: LimitType, period: number | null) => {
  const res = await sendCommand<LimitResponse>(limitSet, { amount, type, period });

  if (res.data) {
    return res.data;
  }

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const getLimit = async (type: LimitType) => {
  const res = await sendCommand<LimitResponse>(limitGet, { type });

  if (res.data) {
    return res.data;
  }

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const enableUserDeposits = async () => {
  const res = await sendCommand<LimitResponse>(userEnableDeposits, {});

  if (res.data) {
    await setUserDetails({
      depositsDisabled: false,
    });
    return res.data;
  }

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const renewSession = async () => {
  const res = await sendCommand<SessionRenewResponse>(sessionRenew, {});

  if (res.data && res.data.token) {
    setTokenDetails(res.data.token);
    return;
  }

  if (res.error && res.error.message) {
    throw new Error(res.error.message);
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const setTokenDetails = (token: string) => {
  localStorage.setItem('sessionToken', token);
  const tokenLatestRenewal = String(new Date().getTime());
  localStorage.setItem('tokenLatestRenewal', tokenLatestRenewal);
};

export const removeTokenDetails = () => {
  localStorage.removeItem('sessionToken');
  localStorage.removeItem('tokenLatestRenewal');
};

export const mustRenewToken = () => {
  const currentTime = new Date().getTime();
  const tokenLatestRenewal = localStorage.getItem('tokenLatestRenewal') || undefined;
  return tokenLatestRenewal ? currentTime - Number(tokenLatestRenewal) >= TOKEN_RENEW_AFTER_TIME : false;
};

export const setRegistrationUserPath = async (userId: number, tag: SIGNUP_OPTION, manual: boolean) => {
  const res = await sendCommand<RegistrationUserPathResponse>(userRegistrationUserPath, { userId, tag, manual });

  if (res && res.error) {
    throw new Error(res.error.message || DEFAULT_ERROR_MESSAGE);
  }
};

export const isDuplicateCredential = async (
  endpoint: string,
  phoneNumber: string | undefined,
  email: string | undefined,
): Promise<boolean> => {
  const res = await sendCommand<UserDuplicatedPhoneNumberResponse>(endpoint, { phoneNumber, email });

  if (res && res.error) {
    throw new Error(res.error.message || DEFAULT_ERROR_MESSAGE);
  }

  return res.data.isDuplicate;
};

export const getUserProofRequests = async (): Promise<ProofRequest[]> => {
  const res = await sendCommand<UserProofRequestsOverviewResponse>(documentsGetDocsOverview, {});

  if (res && res.error) {
    throw new Error(res.error.message || DEFAULT_ERROR_MESSAGE);
  }

  return res.data.payload.proofRequests;
};

export const uploadDocument = async (proofRequestId: number, categoryId: number, file: File) => {
  const token = localStorage.getItem('sessionToken');
  const formData = new FormData();
  formData.append('file', file);

  const pathName = `document/?categoryId=${categoryId}&proofRequestId=${proofRequestId}`;
  const postUrl = SOCKET_ADDRESS.endsWith('/') ? `${SOCKET_ADDRESS}${pathName}` : `${SOCKET_ADDRESS}/${pathName}`;

  await axios.post(postUrl, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const getUserEmploymentPendingQuestions = async (type?: UserQuestionsTypes) => {
  const res = await sendCommand<GetUserEmploymentPendingQuestionsResponse>(userGetPendingQuestions, { type });

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  if (res && res.error) {
    throw new Error(res.error.message || DEFAULT_ERROR_MESSAGE);
  }

  return res.data.payload;
};

export const submitUserEmploymentQuestionsAnswers = async (payload: SetUserEmploymentQuestionsAnswersPayload) => {
  const res = await sendCommand<SetUserEmploymentQuestionsAnswersResponse>(userSubmitQuestionsAnswers, payload);

  if (res && res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  if (res && res.error) {
    throw new Error(res.error.message || DEFAULT_ERROR_MESSAGE);
  }

  return;
};

export const setUserLanguage = async (language: string): Promise<void> => {
  const res = await sendCommand<SetUserLanguageResponse>(userSetLanguage, { language });

  if (res && res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  if (res && res.error) {
    throw new Error(res.error.message || DEFAULT_ERROR_MESSAGE);
  }
};

export const getUserZendeskToken = async (): Promise<string> => {
  const res = await sendCommand<SetUserZendeskTokenResponse>(userZendeskAuthenticate, {});

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  if (res && res.error) {
    throw new Error(res.error.message || DEFAULT_ERROR_MESSAGE);
  }

  if (res.data && res.data.token) {
    return res.data.token;
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const getUserAchievements = async () => {
  const res = await sendCommand<GetUserStatsResponse>(personalStatsGet, {});

  if (res && res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  if (res && res.error) {
    throw new Error(res.error.message || DEFAULT_ERROR_MESSAGE);
  }

  if (res && res.data) {
    return res.data;
  }

  throw new Error();
};
